import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import {
  clientTraysItemsSelector,
  clientTraysRemoteTotalSelector,
  isLoadingClientTraysSelector,
  getClientTrayInventoriesListAction,
  cleanClientTraysAction,
} from 'state/slices/clients';
import { usePagination } from 'hooks/pagination';
import {
  InventoryOwner,
  PaginationData,
  Tray,
  TrayInventorySummary,
} from 'constants/interface';
import { Search, Table } from 'UI';

import {
  SearchContainer,
} from './style';

interface InventoryViewProps {
  shouldReload: boolean
  onReloadFinish(): void
}

const InventoryView = ({
  shouldReload,
  onReloadFinish,
}: InventoryViewProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(clientTraysRemoteTotalSelector);

  const inventoryItems = useSelector(clientTraysItemsSelector);
  const isRemoteLoading = useSelector(isLoadingClientTraysSelector);

  const inventories = inventoryItems.map((inventory) => {
    const tableInventory: TrayInventorySummary & {
      key?: string,
    } = {
      ...inventory,
      key: `${inventory.tray.id.toString()}/${inventory.owner.id.toString()}`,
    };
    return tableInventory;
  }).filter((tableInventory): tableInventory is TrayInventorySummary => !!tableInventory);

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    const orderByMapping: Record<string, string> = {
      owner: 'client',
    };
    const orderBy = orderByMapping[paginationData.orderBy || ''] || paginationData.orderBy;
    dispatch(getClientTrayInventoriesListAction({
      ...paginationData,
      orderBy,
    }));
  };
  const pagination = usePagination<TrayInventorySummary>(
    remoteFetch,
    {
      filter: {
        client: undefined,
        tray: undefined,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanClientTraysAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  useEffect(() => {
    if (shouldReload) {
      pagination.fetch();
      onReloadFinish();
    }
  }, [shouldReload]);

  const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar no estoque" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table
        dataSource={inventories}
        pagination={pagination.getInternal()}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="Cliente"
          key="client"
          dataIndex="owner"
          render={(
            owner: InventoryOwner,
          ) => owner.name}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'owner' ? pageData.order : undefined}
        />
        <Table.Column
          title="Bandeja"
          key="tray"
          dataIndex="tray"
          render={(tray: Omit<
            Tray,
            'createdAt' |
            'updatedAt' |
            'createdBy' |
            'updatedBy'
            >) => `Bandeja ${tray.capacity}${tray.isHydroponic ? ' Hidropônica' : ''}`}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'tray' ? pageData.order : undefined}
        />
        <Table.Column
          title="Saldo atual"
          key="quantity"
          dataIndex="quantity"
          render={(quantity: number) => {
            const formattedQuantity = Intl
              .NumberFormat(
                'pt-BR',
              ).format(quantity);
            const content = `${formattedQuantity} bandeja${quantity > 1 ? 's' : ''}`;
            return content;
          }}
        />
      </Table>
    </>
  );
};

export default InventoryView;
